import type { CollectionName, CollectionTypes } from "./types";

export type Filter = { field: string; op: "==" | ">=" | "<="; value: string | number | boolean | null };
export type AnyDoc = { id: string } & Record<string, unknown>;

export interface Reader {
  get(collection: CollectionName, id: string): Promise<AnyDoc | undefined>;
  query(collection: CollectionName, filters: Filter[]): Promise<AnyDoc[]>;
}

export function matches(doc: AnyDoc, filters: readonly Filter[]): boolean {
  return filters.every(({ field, op, value }) => {
    const actual = doc[field] as string | number | boolean | null | undefined;
    if (op === "==") return actual === value;
    if (actual === null || actual === undefined || value === null) return false;
    return op === ">=" ? actual >= value : actual <= value;
  });
}

/**
 * Read-your-writes transaction view. Writes are buffered and committed atomically by the owning
 * store, which also lets Firestore transactions satisfy their reads-before-writes rule.
 * There is deliberately no delete: records are deactivated, voided or reversed, never removed.
 */
export class Tx {
  readonly writes = new Map<CollectionName, Map<string, AnyDoc>>();
  constructor(private readonly reader: Reader) {}

  async get<C extends CollectionName>(collection: C, id: string): Promise<CollectionTypes[C] | undefined> {
    const buffered = this.writes.get(collection)?.get(id);
    return (buffered ?? await this.reader.get(collection, id)) as CollectionTypes[C] | undefined;
  }

  async query<C extends CollectionName>(collection: C, filters: Filter[]): Promise<CollectionTypes[C][]> {
    const buffered = this.writes.get(collection);
    const stored = (await this.reader.query(collection, filters)).filter((doc) => !buffered?.has(doc.id));
    const pending = buffered ? [...buffered.values()].filter((doc) => matches(doc, filters)) : [];
    return [...stored, ...pending] as unknown as CollectionTypes[C][];
  }

  set<C extends CollectionName>(collection: C, doc: CollectionTypes[C]): void {
    if (!doc.id) throw new Error(`Document in ${collection} is missing an id`);
    let buffered = this.writes.get(collection);
    if (!buffered) this.writes.set(collection, buffered = new Map());
    buffered.set(doc.id, structuredClone(doc) as AnyDoc);
  }
}

export interface Store {
  transaction<T>(work: (tx: Tx) => Promise<T>): Promise<T>;
}

export type Change = { collection: CollectionName; doc: AnyDoc };

/** In-memory store used by tests and as the base for snapshot import/export. Transactions are serialized. */
export class MemoryStore implements Store, Reader {
  readonly data = new Map<CollectionName, Map<string, AnyDoc>>();
  private queue: Promise<unknown> = Promise.resolve();
  onCommit?: (changes: Change[]) => void;

  async get(collection: CollectionName, id: string) {
    const doc = this.data.get(collection)?.get(id);
    return doc ? structuredClone(doc) : undefined;
  }
  async query(collection: CollectionName, filters: Filter[]) {
    return [...(this.data.get(collection)?.values() ?? [])].filter((doc) => matches(doc, filters)).map((doc) => structuredClone(doc));
  }
  all<C extends CollectionName>(collection: C): CollectionTypes[C][] {
    return [...(this.data.get(collection)?.values() ?? [])] as unknown as CollectionTypes[C][];
  }
  transaction<T>(work: (tx: Tx) => Promise<T>): Promise<T> {
    const run = this.queue.then(async () => {
      const tx = new Tx(this);
      const result = await work(tx);
      const changes: Change[] = [];
      for (const [collection, docs] of tx.writes) {
        let target = this.data.get(collection);
        if (!target) this.data.set(collection, target = new Map());
        for (const doc of docs.values()) { target.set(doc.id, doc); changes.push({ collection, doc }); }
      }
      this.onCommit?.(changes);
      return result;
    });
    this.queue = run.catch(() => undefined);
    return run;
  }
}
